"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, CircleCheck as CheckCircle2, Clock, Mail, Phone, ShieldCheck, UserCheck, ArrowRight, Sparkles, Loader as Loader2, MessageSquare } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const guaranteeList = [{
  id: 1,
  title: "100% Direct Consultant Access",
  description: "You work directly with me, Yassine Essaadi. No junior staff or account managers."
}, {
  id: 2,
  title: "Tailored Growth Strategy",
  description: "Custom roadmaps designed specifically for your target audience and industry."
}, {
  id: 3,
  title: "Transparent & Honest ROI",
  description: "Clear deliverables, plain-English reporting, and practical implementation."
}, {
  id: 4,
  title: "Zero Pressure Advice",
  description: "An authentic, expert review focused on what will actually move the needle."
}];
function InnerFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 text-center space-y-4 bg-muted/40 rounded-2xl border border-border">
        <div className="w-14 h-14 bg-accent/20 text-accent rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-accent" />
        </div>
        <h3 className="text-xl font-semibold text-foreground">Consultation Request Received</h3>
        <p className="text-muted-foreground text-sm max-w-sm mx-auto">
          {successMessage || "Thank you for reaching out. I will review your website personally and contact you within 24 hours to set up our meeting."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-xl">
          Book Another Session
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Please enter at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Full Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background rounded-xl h-11 px-4 border-input" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Business Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@company.com" className="bg-background rounded-xl h-11 px-4 border-input" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Website URL</FormLabel>
            <FormControl>
              <Input placeholder="https://yourcompany.com" className="bg-background rounded-xl h-11 px-4 border-input" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="goals" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Primary SEO Goal (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Increase qualified leads, improve technical SEO" className="bg-background rounded-xl h-11 px-4 border-input" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground">
              Helps me review your site search presence prior to our call.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-xs rounded-xl">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 rounded-xl text-base font-semibold shadow-md transition-colors duration-200 mt-2">
        {isSubmitting ? <>
            <Loader2 className="animate-spin w-5 h-5 mr-2" />
            Reserving Session...
          </> : <>
            <SafeHydrate><Calendar className="w-5 h-5 mr-2" /></SafeHydrate>
            Book a Free SEO Consultation
          </>}
      </Button>

      <div className="pt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground text-center">
        <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
        <span>100% confidential. No obligation or high-pressure tactics.</span>
      </div>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="relative py-16 md:py-24 bg-background overflow-hidden">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-secondary/30 rounded-full filter blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-0 left-10 w-80 h-80 bg-accent/10 rounded-full filter blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-muted border border-border text-xs font-semibold text-primary">
              <UserCheck className="w-4 h-4 text-accent" />
              <span>Direct Freelance SEO Consultant</span>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground font-default tracking-tight leading-tight">
                Ready for Sustainable Search Growth? Let us Talk Strategy.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                I help UK and international businesses grow organic revenue through tailored SEO strategies. When you book a consultation, you speak directly with me—Yassine Essaadi.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {guaranteeList.map((item, i) => <div key={item.id} data-index={i} className="p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-[border-color,box-shadow] duration-200">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                    <h3 className="font-semibold text-foreground text-sm font-default">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal pl-7">
                    {item.description}
                  </p>
                </div>)}
            </div>

            <div className="pt-4 border-t border-border flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                <a href="mailto:contact@ye-consultancy.com" className="hover:text-foreground transition-colors">
                  contact@ye-consultancy.com
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-primary" />
                <a href="tel:+447490984430" className="hover:text-foreground transition-colors">
                  +44 7490984430
                </a>
              </div>
              <div>
                <Link to="/ContactPage" className="inline-flex items-center font-medium text-primary hover:text-primary/80 transition-colors">
                  Visit Contact Page <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-muted/60 border border-border flex items-center gap-4">
              <div className="relative w-14 h-14 rounded-full overflow-hidden bg-primary/10 shrink-0 border-2 border-primary/20">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_headshot.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_headshot.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_headshot.webp-wvc-sizes" alt="Yassine Essaadi - Freelance SEO Consultant" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="text-xs">
                <p className="font-semibold text-foreground text-sm">
                  Yassine Essaadi
                </p>
                <p className="text-muted-foreground">
                  Independent SEO Consultant • YE Consultancy
                </p>
                <p className="text-primary font-medium mt-0.5">
                  {"\"I personally manage and execute every SEO campaign.\""}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="shadow-lg border-border bg-card/90 backdrop-blur-xs rounded-2xl overflow-hidden">
              <CardHeader className="bg-primary/5 border-b border-border p-6">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                    Free 30-Min Session
                  </span>
                  <div className="flex items-center text-xs text-muted-foreground gap-1">
                    <Clock className="w-3.5 h-3.5 text-accent" />
                    <span>No Obligation</span>
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-foreground font-default">
                  Book a Free SEO Consultation
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Fill in your website details below to schedule a 1-on-1 discovery meeting with Yassine.
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6">
                <FormProvider formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                name: "",
                email: "",
                website: "",
                goals: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                website: {
                  required: true
                }
              }} submitText="Book My Free SEO Consultation" successMessage="Thank you! Your request has been received. I will reach out within 24 hours.">
                  <InnerFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}